import React, { useState, useCallback, useEffect } from 'react';
import ProjectSelector from './components/ProjectSelector';
import ChatInterface from './components/ChatInterface';
import Sidebar from './components/Sidebar';
import PreviewWindow from './components/PreviewWindow';
import AdvancedSettings from './components/AdvancedSettings';
import AuthGuard from './guards/auth.guard';
import apiService, { QuestionOptions, Message, ConversationHistory, ConversationMessageItem } from './services/api';

interface Conversation {
  id: string;
  title: string;
  timestamp: number;
  messageCount: number;
}

interface PreviewItem {
  id: string;
  type: 'table' | 'image' | 'chart' | 'code';
  title: string;
  content: any;
  timestamp: number;
}

function App() {
  // Get project key from URL path (e.g., /ce12c596-2f6b-4327-b2ae-34f96af817a7)
  const getProjectFromUrl = (): string | null => {
    const path = window.location.pathname;
    // Match UUID pattern in URL path
    const uuidMatch = path.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    return uuidMatch ? uuidMatch[1] : null;
  };

  // Priority: URL > localStorage
  const [selectedProject, setSelectedProject] = useState<string | null>(() => {
    const urlProject = getProjectFromUrl();
    if (urlProject) {
      console.log(`✓ Using project from URL: ${urlProject}`);
      return urlProject;
    }
    return localStorage.getItem('selectedProject') || null;
  });
  const [userId, setUserId] = useState<string>(() => {
    return localStorage.getItem('userId') || 'anonymous';
  });

  // Sync with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const handleUrlChange = () => {
      const urlProject = getProjectFromUrl();
      if (urlProject && urlProject !== selectedProject) {
        console.log(`✓ URL changed, switching to project: ${urlProject}`);
        setSelectedProject(urlProject);
        setActiveConversation(null);
        setHistoricalMessages([]);
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [selectedProject]);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [historicalMessages, setHistoricalMessages] = useState<Message[]>([]);
  // Local unsaved conversation (only for "New Chat" that hasn't been sent to backend yet)
  const [pendingConversation, setPendingConversation] = useState<Conversation | null>(null);
  // Trigger to reload conversation history (increment to trigger reload)
  const [conversationRefreshTrigger, setConversationRefreshTrigger] = useState(0);
  // Track which conversations have pending requests
  const [loadingConversations, setLoadingConversations] = useState<Set<string>>(new Set());
  const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);
  const [selectedPreviewItem, setSelectedPreviewItem] = useState<string | null>(null);
  
  // Settings state
  const [executionMode, setExecutionMode] = useState<'basic' | 'extended'>('basic');
  const [personalityMode, setPersonalityMode] = useState<'simple' | 'professional' | 'teacher'>('professional');
  const [advancedOptions, setAdvancedOptions] = useState<Partial<QuestionOptions>>({
    enable_query_rewriting: true,
    enable_confidence_scoring: true,
    enable_abstention: true,
    confidence_threshold: 0.30,
    search_strategy: 'hybrid',
    top_k_agents: 5,
    timeout: 60
  });
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);
  // undefined: the backend config has not answered yet. null: no default project (or the config failed).
  const [defaultProjectKey, setDefaultProjectKey] = useState<string | null | undefined>(undefined);

  // Fetch backend config to determine debug mode and default project
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:7003/api';
        const response = await fetch(`${apiUrl}/ie/config`);
        if (!response.ok) {
          throw new Error(`Config fetch failed: HTTP ${response.status}`);
        }
        const config = await response.json();
        setIsDebugMode(config.debug === true);
        setDefaultProjectKey(config.default_project_key || null);
      } catch (err) {
        console.error('Failed to fetch config:', err);
        setIsDebugMode(false);
        setDefaultProjectKey(null); // the selector must not wait for ever: it falls back to the first project
      }
    };

    fetchConfig();
  }, []);

  // Persist selectedProject to localStorage
  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('selectedProject', selectedProject);
      console.log(`✓ Persisted selected project: ${selectedProject}`);
    }
  }, [selectedProject]);

  // Persist userId to localStorage
  useEffect(() => {
    if (userId) {
      localStorage.setItem('userId', userId);
      console.log(`✓ Persisted user ID: ${userId}`);
    }
  }, [userId]);

  const handleNewChat = useCallback(() => {
    // Create a temporary conversation ID for the new chat
    const newConversationId = `conv_${Date.now()}`;
    const newConversation: Conversation = {
      id: newConversationId,
      title: 'New Conversation',
      timestamp: Date.now(),
      messageCount: 0
    };
    
    // Set as pending (not saved to backend yet)
    setPendingConversation(newConversation);
    setActiveConversation(newConversationId);
    setHistoricalMessages([]); // Clear messages for new conversation
    console.log(`✓ Created new chat (pending): ${newConversationId}`);
  }, []);

  const handleConversationSelect = useCallback(async (conversationData: ConversationHistory) => {
    if (!selectedProject) {
      console.error('Cannot select conversation: No project selected');
      return;
    }
    
    try {
      // Fetch all messages for this conversation using the new multi-turn API
      // Use session_id which contains the original "conv_..." ID string
      const messages = await apiService.getConversationMessages(
        conversationData.session_id,
        selectedProject
      );
      
      // Convert to Message format for ChatInterface
      const formattedMessages: Message[] = messages.map((msg: ConversationMessageItem) => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: msg.timestamp
      }));
      
      setHistoricalMessages(formattedMessages);
      // Use session_id (the actual conv_xxx ID) instead of numeric id
      setActiveConversation(conversationData.session_id);
      
      console.log(`✓ Loaded ${formattedMessages.length} messages for conversation ${conversationData.session_id}`);
    } catch (error) {
      console.error('Failed to load conversation messages:', error);
      // Fallback to old behavior (single Q&A pair)
      const messages: Message[] = [
        {
          role: 'user',
          content: conversationData.query,
          timestamp: new Date(conversationData.created_at).getTime()
        },
        {
          role: 'assistant',
          content: conversationData.response,
          timestamp: new Date(conversationData.created_at).getTime() + 1
        }
      ];
      setHistoricalMessages(messages);
      setActiveConversation(conversationData.session_id);
    }
  }, [selectedProject]);

  const handlePreviewItemSelect = useCallback((id: string) => {
    setSelectedPreviewItem(id);
  }, []);

  const handleExecutionModeChange = useCallback((mode: 'basic' | 'extended') => {
    setExecutionMode(mode);
  }, []);

  const handlePersonalityModeChange = useCallback((mode: 'simple' | 'professional' | 'teacher') => {
    setPersonalityMode(mode);
  }, []);

  const handleAdvancedSettingsOpen = useCallback(() => {
    setShowAdvancedSettings(true);
  }, []);

  const handleActiveConversationChange = useCallback((conversationId: string) => {
    setActiveConversation(conversationId);
    // Clear pending conversation when backend assigns a real ID
    setPendingConversation(null);
    // Trigger conversation history reload to show the new conversation with proper title
    setConversationRefreshTrigger(prev => prev + 1);
    console.log(`✓ Active conversation updated to: ${conversationId}, triggering history refresh`);
  }, []);

  // Handler for when a conversation is created but user has navigated away
  const handleConversationCreated = useCallback(() => {
    // Clear pending conversation
    setPendingConversation(null);
    // Trigger conversation history reload without changing active conversation
    setConversationRefreshTrigger(prev => prev + 1);
    console.log('✓ New conversation created (user switched away), refreshing history');
  }, []);

  const handleProjectChange = useCallback((projectKey: string | null) => {
    console.log(`✓ Switching to project: ${projectKey}`);
    setSelectedProject(projectKey);
    // Clear active conversation and messages when switching projects
    setActiveConversation(null);
    setHistoricalMessages([]);
    console.log(`✓ Cleared active conversation and messages`);
  }, []);

  return (
    <AuthGuard>
      {/* 100dvh (when supported) tracks the *visible* mobile viewport; 100vh includes
          the collapsed URL bar and pushes the bottom-sticky input off-screen. */}
      <div className="h-screen supports-[height:100dvh]:h-dvh bg-ie-pale text-ie-navy flex flex-col overflow-hidden">
        <ProjectSelector
          selectedProject={selectedProject}
          onProjectSelect={handleProjectChange}
          defaultProjectKey={defaultProjectKey}
        />
        <div className="flex-1 flex overflow-hidden relative">
          {/* Left Sidebar */}
          <Sidebar
              activeConversation={activeConversation}
            pendingConversation={pendingConversation}
          onHistoryConversationSelect={handleConversationSelect}
            onNewChat={handleNewChat}
            executionMode={executionMode}
            onExecutionModeChange={handleExecutionModeChange}
            personalityMode={personalityMode}
            onPersonalityModeChange={handlePersonalityModeChange}
            onAdvancedSettingsOpen={handleAdvancedSettingsOpen}
            userId={userId}
          projectKey={selectedProject || ''}
          refreshTrigger={conversationRefreshTrigger}
          loadingConversations={loadingConversations}
        />
          
          {/* Main Chat Interface */}
          <main className="flex-1 flex flex-col min-w-0">
            <ChatInterface
              selectedProject={selectedProject}
              activeConversation={activeConversation}
              executionMode={executionMode}
              personalityMode={personalityMode}
              advancedOptions={advancedOptions}
              onActiveConversationChange={handleActiveConversationChange}
            onConversationCreated={handleConversationCreated}
            onNewChat={handleNewChat}
            userId={userId}
            historicalMessages={historicalMessages}
            onLoadingConversationsChange={setLoadingConversations}
            />
          </main>
          
          {/* Right Preview Window - Only visible in debug mode */}
          {isDebugMode && (
            <PreviewWindow
              items={previewItems}
              selectedItem={selectedPreviewItem}
              onItemSelect={handlePreviewItemSelect}
            />
          )}
          
          {/* Advanced Settings Modal */}
          <AdvancedSettings
            isOpen={showAdvancedSettings}
            onClose={() => setShowAdvancedSettings(false)}
            options={advancedOptions}
            onOptionsChange={setAdvancedOptions}
          />
        </div>
      </div>
    </AuthGuard>
  );
}

export default App;
